'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CREATED_AT_DATE_FILTER_ID,
  getClearedCreatedAtDateFilterValue,
  resolveCreatedAtDateRange,
  useCreatedAtDateFilter,
  type CreatedAtDateFilterValue,
  type FilterConfig,
  type FilterValue,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { useExternalNetworkAssetFilterOptions } from '../../../hooks/api/useSurfaceAssets';
import {
  buildAssetFilterConfigs,
  EMPTY_ASSET_FILTER_OPTIONS,
} from './build-asset-filter-configs';
import type { ExternalNetworkAssetListFilters } from './build-asset-list-query';
import { parseAssetScopeFilter } from './asset-scope-filter';
import type { ExternalNetworkEntityKindFilter } from './surfaceAsset.constants';

export type AssetFilterRowConfig = {
  filters: FilterConfig[];
  filterValue: FilterValue;
  onFilterChange: (value: FilterValue) => void;
};

function extractFilterValue(
  filterValue: FilterValue,
  id: string,
): string | undefined {
  return filterValue.find((filter) => filter.id === id)?.value[0];
}

export function useExternalNetworkAssetListFilters({
  selectedKind,
  surfaceId,
}: {
  selectedKind: ExternalNetworkEntityKindFilter;
  surfaceId: string;
}) {
  const t = useTranslations('externalNetwork');
  const tEntityKinds = useTranslations('externalNetwork.entityKinds');
  const dateFormatter = useDateFormatter();
  const { options: assetFilterOptions } =
    useExternalNetworkAssetFilterOptions(surfaceId);
  const [createdAtDateFilter, setCreatedAtDateFilter] =
    useState<CreatedAtDateFilterValue>(getClearedCreatedAtDateFilterValue);
  const [assetFilterValue, setAssetFilterValue] = useState<FilterValue>([]);

  useEffect(() => {
    setAssetFilterValue([]);
    setCreatedAtDateFilter(getClearedCreatedAtDateFilterValue());
  }, [selectedKind]);

  const parsedAssetFilters = useMemo<ExternalNetworkAssetListFilters>(() => {
    const product = extractFilterValue(assetFilterValue, 'product');
    const softwareType = extractFilterValue(assetFilterValue, 'softwareType');
    const cveId = extractFilterValue(assetFilterValue, 'cveId');
    const region = extractFilterValue(assetFilterValue, 'region');
    const organization = extractFilterValue(assetFilterValue, 'organization');
    const scope = parseAssetScopeFilter({
      value: extractFilterValue(assetFilterValue, 'scope'),
    });

    return {
      ...(product ? { product } : {}),
      ...(softwareType ? { softwareType } : {}),
      ...(cveId ? { cveId } : {}),
      ...(region ? { region } : {}),
      ...(organization ? { organization } : {}),
      ...(scope ? { scope } : {}),
    };
  }, [assetFilterValue]);

  const createdAtDateRange = useMemo(
    () => resolveCreatedAtDateRange({ filter: createdAtDateFilter }),
    [createdAtDateFilter],
  );

  const createdAtDateFilterControls = useCreatedAtDateFilter({
    filter: createdAtDateFilter,
    onFilterChange: setCreatedAtDateFilter,
    formatDate: (date) => dateFormatter.formatDateOnly(date),
  });

  const withCreatedAtDateFilter = useCallback(
    (config: AssetFilterRowConfig): AssetFilterRowConfig => ({
      ...config,
      filters: [...config.filters, createdAtDateFilterControls.filterConfig],
      filterValue: [
        ...config.filterValue,
        ...createdAtDateFilterControls.filterValue,
      ],
      onFilterChange: (value) => {
        const dateFilterValue = value.filter(
          (entry) => entry.id === CREATED_AT_DATE_FILTER_ID,
        );
        const otherFilterValue = value.filter(
          (entry) => entry.id !== CREATED_AT_DATE_FILTER_ID,
        );
        const previousDatePreset =
          createdAtDateFilterControls.filterValue[0]?.value[0];
        const nextDatePreset = dateFilterValue[0]?.value[0];
        const didDatePresetChange =
          Boolean(nextDatePreset) && nextDatePreset !== previousDatePreset;

        if (dateFilterValue.length > 0) {
          createdAtDateFilterControls.onFilterChange(dateFilterValue);
        } else {
          createdAtDateFilterControls.onFilterChange([]);
        }

        if (!didDatePresetChange) {
          config.onFilterChange(otherFilterValue);
        }
      },
    }),
    [createdAtDateFilterControls],
  );

  const assetFilterRowConfig = useMemo(() => {
    const assetsConfig: AssetFilterRowConfig = {
      filters: buildAssetFilterConfigs({
        selectedKind,
        softwareLabel: tEntityKinds('software'),
        technologyLabel: t('assetFilters.technology'),
        cveLabel: t('assetFilters.cve'),
        regionLabel: t('assetFilters.region'),
        organizationLabel: t('assetFilters.organization'),
        scopeLabel: t('assetDetail.scope'),
        options: assetFilterOptions ?? EMPTY_ASSET_FILTER_OPTIONS,
      }),
      filterValue: assetFilterValue,
      onFilterChange: setAssetFilterValue,
    };

    return withCreatedAtDateFilter(assetsConfig);
  }, [
    assetFilterOptions,
    assetFilterValue,
    selectedKind,
    t,
    tEntityKinds,
    withCreatedAtDateFilter,
  ]);

  const listFilters = useMemo<ExternalNetworkAssetListFilters>(
    () => ({
      ...parsedAssetFilters,
      ...(createdAtDateRange.createdStartDate
        ? { createdStartDate: createdAtDateRange.createdStartDate }
        : {}),
      ...(createdAtDateRange.createdEndDate
        ? { createdEndDate: createdAtDateRange.createdEndDate }
        : {}),
    }),
    [createdAtDateRange, parsedAssetFilters],
  );

  const hasListFilters =
    Object.values(listFilters).some((value) => value !== undefined) ||
    assetFilterValue.length > 0;

  return {
    assetFilterRowConfig,
    createdAtDateFilterDialog: createdAtDateFilterControls.customRangeDialog,
    listFilters,
    hasListFilters,
  };
}
